import React from "react";
import { IMAGE_BASE_URL } from "../../config/api";

const formatCurrency = (val, currencyCode) => {
  if (val === undefined || val === null || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);
  try {
    if (currencyCode) {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode, maximumFractionDigits: 2 }).format(num);
    }
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(num);
  } catch (_) {
    return String(val);
  }
};

const getValueByKey = (key, item) => {
  switch (key) {
    case "Description":
      return item?.description || "";
    case "Project":
      return item?.project?.title || item?.project_id || "";
    case "Amount":
      return item?.amount ?? "";
    case "Tax":
      return item?.tax_amount ?? "-";
    case "Total Amount":
      return item?.input_amount ?? item?.amount ?? "";
    case "Period":
      return item?.invoice_date ? item?.invoice_date?.slice(0, 10) : "";
    case "Vendor":
      return item?.vendor?.title || "";
    default:
      return "";
  }
};

const ExportCard = ({ item, index = 0, selectedColumns = [], allColumnKeys = [], currencyCode }) => {
  const columnsToUse = selectedColumns.length ? selectedColumns : allColumnKeys;
  const title = item?.project?.title || item?.project_id ? `Expense • ${(item?.project?.title || item?.project_id)}` : `Expense #${index + 1}`;

  const numFields = columnsToUse.length;
  const isSmall = numFields <= 4;
  const cardWidth = isSmall ? 720 : 980;
  const labelFont = isSmall ? 13 : 12;
  const valueFont = isSmall ? 15 : 13;
  const rowGap = isSmall ? 12 : 8;
  const colGap = isSmall ? 16 : 12;

  const normalizeImageUrl = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const cleaned = String(pathOrUrl).startsWith("/") ? String(pathOrUrl).slice(1) : String(pathOrUrl);
    const base = IMAGE_BASE_URL.endsWith("/") ? IMAGE_BASE_URL : IMAGE_BASE_URL + "/";
    return base + cleaned;
  };

  const images = Array.isArray(item?.invoice_image)
    ? item.invoice_image
    : (item?.invoice_image ? [item.invoice_image] : []);

  const isPdf = (path) => {
    if (!path) return false;
    const str = String(path).toLowerCase();
    return /\.pdf(?:[?#]|$)/.test(str);
  };

  const imageThumbs = images.filter((p) => !isPdf(p));
  const pdfCount = images.length - imageThumbs.length;

  return (
    <div
      style={{
        width: cardWidth,
        background: "#ffffff",
        color: "#0f172a",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 0,
        margin: 12,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
      }}
    >
      <div
        style={{
          background: "#F8FAFF",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          padding: isSmall ? "16px 18px" : "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eef2ff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: 9999, background: "#1E3A8A" }} />
          <div style={{ fontSize: isSmall ? 17 : 15, fontWeight: 700 }}>{title}</div>
        </div>
      </div>

      <div style={{ padding: isSmall ? "16px 18px 18px" : "12px 14px 14px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            rowGap,
            columnGap: colGap,
          }}
        >
          {columnsToUse.map((key) => {
            let value = getValueByKey(key, item);
            if (key === "Amount" || key === "Total Amount") value = formatCurrency(value, currencyCode);
            return (
              <React.Fragment key={key}>
                <div style={{ fontWeight: 600, fontSize: labelFont, color: "#475569" }}>{key}</div>
                <div style={{ fontSize: valueFont, fontWeight: 600, color: "#0f172a", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{String(value ?? "-")}</div>
              </React.Fragment>
            );
          })}
        </div>

        {images.length > 0 && (
          <div style={{ height: isSmall ? 12 : 8 }} />
        )}

        {images.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              alignItems: "start",
              columnGap: 12,
              marginTop: 4,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: labelFont, color: "#475569" }}>Invoice Attachment{images.length > 1 ? "s" : ""}:</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {imageThumbs.slice(0, 3).map((img, idx) => (
                <img
                  key={idx}
                  src={normalizeImageUrl(img)}
                  alt={`Invoice ${idx + 1}`}
                  style={{
                    width: isSmall ? 160 : 120,
                    height: isSmall ? 160 : 120,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    marginTop: 2,
                    background: "#fafafa",
                  }}
                />
              ))}
              {pdfCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: isSmall ? 160 : 120,
                    height: isSmall ? 160 : 120,
                    borderRadius: 8,
                    border: "1px dashed #ef4444",
                    color: "#b91c1c",
                    fontSize: valueFont,
                    fontWeight: 700,
                    background: "#fff1f2",
                  }}
                >
                  {pdfCount} PDF{pdfCount > 1 ? "s" : ""}
                </div>
              )}
              {imageThumbs.length > 3 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: isSmall ? 160 : 120,
                    height: isSmall ? 160 : 120,
                    borderRadius: 8,
                    border: "1px dashed #cbd5e1",
                    color: "#475569",
                    fontSize: valueFont,
                    fontWeight: 600,
                  }}
                >
                  +{imageThumbs.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportCard;


